import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { toast } from 'sonner';

import {
    ChainlitContext,
    threadHistoryState,
    useChatInteract
} from '@chainlit/react-client';

interface UseThreadActionsOptions {
    threadId: string | undefined;
    isPinned: boolean;
    canDeleteThread: boolean;
    mutateApiThread: () => void;
}

export function useThreadActions({
    threadId,
    isPinned,
    canDeleteThread,
    mutateApiThread
}: UseThreadActionsOptions) {
    const navigate = useNavigate();
    const apiClient = useContext(ChainlitContext);
    const setThreadHistory = useSetRecoilState(threadHistoryState);
    const { clear } = useChatInteract();
    const handlePin = async () => {
        if (!threadId) return;
        try {
            await apiClient.updateThread(threadId, { isPinned: true });
            setThreadHistory((prev) => ({
                ...prev,
                threads: prev?.threads?.filter((t) => t.id !== threadId)
            }));
            window.dispatchEvent(
                new CustomEvent('thread-pin-changed', {
                    detail: { threadId }
                })
            );
            toast.success('Thread pinned to organization');
            navigate('/');
        } catch (err) {
            console.error('Failed to pin thread:', err);
            toast.error('Failed to pin thread');
        }
    };

    const handleUnpin = async () => {
        if (!threadId) return;
        try {
            await apiClient.updateThread(threadId, { isPinned: false });
            window.dispatchEvent(
                new CustomEvent('thread-pin-changed', {
                    detail: { threadId }
                })
            );
            mutateApiThread();
            toast.success('Thread unpinned');
        } catch (err) {
            console.error('Failed to unpin thread:', err);
            toast.error('Failed to unpin thread');
        }
    };

    const handlePinToggle = isPinned ? handleUnpin : handlePin;

    const handleDelete = async () => {
        if (!threadId || !canDeleteThread) return;
        clear();
        navigate('/');
        setThreadHistory((prev) => ({
            ...prev,
            threads: prev?.threads?.filter((t) => t.id !== threadId)
        }));
        try {
            await apiClient.deleteThread(threadId);
        } catch (err) {
            console.error('Failed to delete thread:', err);
            toast.error('Failed to delete thread');
        }
    };

    return {
        handlePinToggle,
        handleDelete
    };
}
